import { confirmUnsavedChanges } from '@/shared/lib/confirmUnsavedChanges';
import { useUnsavedForm } from '@/shared/hooks/useUnsavedForm';
import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider } from 'react-hook-form';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { useOrders } from '@/entities/Order/model/store';
import {
    ORDER_COMMENT_ACTION_DEFAULTS,
    orderCommentActionSchema,
    type OrderCommentActionFormValues,
} from '@/entities/Order/model/orderCommentActionSchema';
import { Modal } from '@/shared/ui/Modal/Modal';
import { RequestEmpty, RequestError, RequestLoading } from '@/shared/ui/RequestState';
import { createRHFFields } from '@/shared/ui/RHFFields';
import { useOrderCommentActionSubmit, type OrderCommentAction } from './useOrderCommentActionSubmit';
import classes from './OrderCommentActionModal.module.scss';

export interface OrderCommentActionModalProps {
    visible: boolean;
    onHide: () => void;
    orderId: string;
    action: OrderCommentAction;
}

const ACTION_CONTENT = {
    approveExecutor: {
        title: 'Утвердить исполнителя на заказ',
        submitLabel: 'Утвердить',
        successMessage: 'Исполнитель успешно утвержден',
        errorMessage: 'Не удалось утвердить исполнителя',
        icon: 'pi pi-user-plus',
    },
    startExecutorSearch: {
        title: 'Поиск исполнителя по заказу',
        submitLabel: 'Начать поиск',
        successMessage: 'Поиск исполнителя успешно начат',
        errorMessage: 'Не удалось начать поиск исполнителя',
        icon: 'pi pi-search',
    },
} satisfies Record<OrderCommentAction, {
    title: string;
    submitLabel: string;
    successMessage: string;
    errorMessage: string;
    icon: string;
}>;

const { TextField: CommentField } = createRHFFields<OrderCommentActionFormValues>();

/** Показывает форму действия с исполнителем заказа. */
export const OrderCommentActionModal = (
    {
        visible,
        onHide,
        orderId,
        action,
    }: OrderCommentActionModalProps
) => {
    const content = ACTION_CONTENT[action];

    const form = useUnsavedForm<OrderCommentActionFormValues>({
        resolver: zodResolver(orderCommentActionSchema),
        defaultValues: ORDER_COMMENT_ACTION_DEFAULTS,
    }, visible);

    const { onSubmit, completed, resetCompleted } = useOrderCommentActionSubmit(
        action,
        orderId,
        form,
        content.successMessage,
        content.errorMessage,
    );

    const getOrderById = useOrders((state) => state.getOrderById);
    const { reset } = form;

    useEffect(() => {
        if (!visible) return;

        reset(ORDER_COMMENT_ACTION_DEFAULTS);
        resetCompleted();
    }, [action, orderId, visible, reset, resetCompleted]);

    /** Закрывает окно и обновляет заказ после успешного действия. */
    const handleHide = () => confirmUnsavedChanges(form.formState.isDirty, () => {
        reset();

        if (completed) void getOrderById(orderId);
        onHide();
    });

    const isSubmitting = form.formState.isSubmitting;
    const hasOrderId = Boolean(orderId.trim());

    return <Modal
        visible={visible}
        onHide={handleHide}
        title={content.title}
        size="lg"
        className={classes.modal}
        closable={!isSubmitting}
        footer={<div className={classes.footer}>
            <Button
                type="button"
                label={completed
                    ? 'Закрыть'
                    : 'Отмена'
                }
                text
                onClick={handleHide}
                disabled={isSubmitting}
            />
            {!completed && hasOrderId &&
                <Button
                    type="submit"
                    form="order-comment-action-form"
                    label={content.submitLabel}
                    icon={content.icon}
                    loading={isSubmitting}
                    disabled={isSubmitting}
                />
            }
        </div>}
    >
        {!hasOrderId &&
            <RequestEmpty message="Номер заказа не найден" />
        }
        {hasOrderId && completed &&
            <Message
                severity="success"
                text={content.successMessage}
            />
        }
        {hasOrderId && !completed &&
            <FormProvider {...form}>
                <form
                    id="order-comment-action-form"
                    onSubmit={form.handleSubmit(onSubmit)}
                    className={classes.form}
                    noValidate
                >
                    <CommentField
                        name="comment"
                        label="Комментарий"
                        placeholder="Комментарий"
                        disabled={isSubmitting}
                    />
                    {isSubmitting &&
                        <RequestLoading message="Обрабатываем заказ..." />
                    }
                    {form.formState.errors.root?.server?.message &&
                        <RequestError
                            message={form.formState.errors.root.server.message}
                            compact
                        />
                    }
                </form>
            </FormProvider>
        }
    </Modal>;
};
